import { useEffect, useMemo, useState } from 'react';
import { safeJsonParse, useFeatureHubData } from '@cyberint/common-utils';
import { Themes } from '../style/theme-consts';

const POINT_DS_CHANGE = 'argos:point-ds-change';
const POINT_DS_CONFIG_FLAG = 'app.point_ds.config';

let pointDsEnabled = false;

export const setPointDs = enabled => {
  pointDsEnabled = !!enabled;
};

const isShowPointDs = () => {
  try {
    return localStorage.getItem('argos.theme') === 'point-ds';
  } catch {
    return false;
  }
};

const isPointDsDisabled = () => {
  try {
    return localStorage.getItem('argos.theme') === 'none';
  } catch {
    return false;
  }
};

/** Sync and React-free on purpose. window.argosParams is populated by an inline script in
  index.html before any bundle runs, so this is answerable at module-load time - which is what
  lets the shell entry apply the theme class to <body> before the first render, instead of after
  a feature-flag round trip inside React. setPointDs is the Storybook override. */
const isPointDS = () => pointDsEnabled || isShowPointDs();

/* Each federated remote bundles its own copy of this module, so the <body> class (not module
  state) is the live source of truth, and the change is broadcast on window. */
const isPointDSApplied = () => document.body.classList.contains(Themes.POINT);

const subscribe = onChange => {
  window.addEventListener(POINT_DS_CHANGE, onChange);
  return () => window.removeEventListener(POINT_DS_CHANGE, onChange);
};

/* Not React's useSyncExternalStore: the IOC extension embeds this bundle under React 17, which lacks it. */
const useExternalStore = (subscribeFn, getSnapshot) => {
  const [value, setValue] = useState(getSnapshot);
  useEffect(() => {
    const sync = () => setValue(getSnapshot());
    sync();
    return subscribeFn(sync);
  }, [subscribeFn, getSnapshot]);
  return value;
};

/** For render-time reads, so components re-render when the theme is toggled live. */
const usePointDS = () => useExternalStore(subscribe, isPointDSApplied);

/** 'light' | 'dark' under Point, undefined otherwise. Reads <body> so it works without the provider. */
const getPointThemeMode = () => document.body.dataset.pointTheme;
const usePointThemeMode = () => useExternalStore(subscribe, getPointThemeMode);

const notifyPointDSChange = () => window.dispatchEvent(new Event(POINT_DS_CHANGE));

const setPointDSApplied = enabled => {
  try {
    localStorage.setItem('argos.theme', enabled ? 'point-ds' : 'none');
  } catch {
    /* storage blocked — the toggle still applies for this session */
  }
  // Set by the index.html boot script; it paints the Point mesh on <html> until removed.
  document.documentElement.classList.remove('point-ds-loading');
  document.body.classList.toggle(Themes.POINT, enabled);
  notifyPointDSChange();
};

const usePointDsConfig = () => {
  const { flags } = useFeatureHubData();
  const rawConfig = flags?.[POINT_DS_CONFIG_FLAG];
  return useMemo(() => safeJsonParse(rawConfig), [rawConfig]);
};

export const PointUtils = {
  isPointDS,
  usePointDS,
  usePointThemeMode,
  notifyPointDSChange,
  usePointDsConfig,
  isPointDsDisabled,
  setPointDSApplied
};
